import { toast } from "react-toastify";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import html2canvas from "html2canvas";
import * as referralCodes from "referral-codes";
import { UAParser } from "ua-parser-js";

import { COUNTY_DETAILS, LOADING_STATUSES, RESIDENT_TYPES } from "./constants";

dayjs.extend(customParseFormat);

export const setStorage = (key, value) => {
  if (typeof value === "string") {
    localStorage.setItem(key, value);
  } else {
    localStorage.setItem(key, JSON.stringify(value));
  }
};

export const getStorage = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key));
  } catch (err) {
    return localStorage.getItem(key);
  }
};

export const isValidFileType = (files) => {
  if (!files || files.length === 0) {
    return false;
  }
  const validExtensions = ["jpg", "jpeg", "png", "xlsx", "pdf", "csv", "doc", "docx", "txt"];

  for (const file of Array.from(files)) {
    const fileName = file.name.split(".");
    const fileExtension = fileName[fileName.length - 1].toLowerCase();

    if (!validExtensions.includes(fileExtension)) {
      return false;
    }
  }
  return true;
};

export const removeStorage = (key) => localStorage.removeItem(key);

export const isEmpty = (object) => {
  if (Array.isArray(object)) {
    return !object.length;
  }

  if (typeof object === "object" && !Array.isArray(object) && object !== null) {
    return !Object.keys(object).length;
  }

  if (typeof object === "string") {
    return !object.length;
  }

  return true;
};

export const getCountDetails = (details = "") => {
  return COUNTY_DETAILS[details.replace(/\s/g, "").toLowerCase()] || COUNTY_DETAILS.default;
};

export const generateAddressLink = (item = {}) => {
  if (item.county.toLowerCase() === "harris") {
    return getCountDetails(item.county)?.link?.url;
  }

  if (item.county.toLowerCase() === "montgomery" && item.account_number) {
    const accountNumber = item.account_number.startsWith("R") ? item.account_number.substring(1) : item.account_number;
    return getCountDetails(item.county)?.link?.url + accountNumber + getCountDetails(item.county)?.link?.info;
  }

  if (item.county) {
    if (item.account_number) {
      return getCountDetails(item.county)?.link?.url + item.account_number + getCountDetails(item.county)?.link?.info;
    } else {
      return getCountDetails(item.county)?.link?.search_url;
    }
  }
};

export const preloadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve(img);
    };
    img.onerror = img.onabort = () => {
      reject(src);
    };
    img.src = src;
  });

export const snakeToCamel = (snakeString) => {
  let camelString = "";
  let isSnake = false;
  for (let i = 0; i < snakeString.length; i++) {
    if (snakeString[i] === "_") {
      isSnake = true;
    } else {
      if (isSnake) {
        camelString += snakeString[i].toUpperCase();
        isSnake = false;
      } else {
        camelString += snakeString[i];
      }
    }
  }

  return camelString;
};

export const getPaginationDescription = (paginationData, lastPage) => {
  return `${(paginationData.page - 1) * paginationData.rows + 1} - 
            ${
              paginationData.rows * paginationData.page >= lastPage
                ? lastPage
                : paginationData.rows * paginationData.page
            } of ${lastPage}`;
};

export const downloadFileByLink = (status, href, name = "properties.csv") => {
  if (status === LOADING_STATUSES.succeeded) {
    const link = document.createElement("a");
    link.href = href;
    link.setAttribute("download", name);
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(href);
  }
};

export const generateReferralCode = () => {
  return referralCodes.generate({
    length: 7,
    count: 3,
    charset: "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ",
  })[0];
};

export const toggleClassOnRoot = () => document.getElementById("root").classList.toggle("prevent-scroll-desktop");

export const numberToDollar = (value) => {
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

  return formatter.format(value);
};

export const toastify = (type, message, className, timeout = 5000, position = "top-right") => {
  const toastId = toast[type](message, {
    position: position,
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: "colored",
    className: className,
    autoClose: false,
  });

  setTimeout(() => {
    toast.dismiss(toastId);
  }, timeout);
};

export const getFileType = (filename) => filename.split(".").pop().toLowerCase();

export const getStatusColor = (status) => {
  switch (status) {
    case "pending":
      return "red";
    case "reviewed":
      return "gray";
    case "worked":
      return "green";
    default:
      return "green";
  }
};

export const getImageFromTypedSignature = async ({ typedSignRef, signature, signType }) => {
  //todo check
  await document.fonts.ready;
  await new Promise((res) => setTimeout(res, 500));

  const canvasElement = typedSignRef.current;
  const textElementHeight = 60;
  const additionalHeight = 10;
  const totalImageHeight = textElementHeight + additionalHeight;

  const options = {
    height: totalImageHeight,
  };

  let result = signature;

  if (signType === "type") {
    const canvas = await html2canvas(canvasElement, options);
    result = canvas.toDataURL("image/png");
  }

  return result;
};

export function transformToClassicDate(date) {
  if (!date) return "";

  const parsedDate = dayjs(date, "YY-MM-DD");
  const day = parsedDate.date();
  const month = parsedDate.format("MMMM");

  if (!day) return "";

  let suffix = "th";

  if (day % 10 === 1 && day !== 11) suffix = "st";
  else if (day % 10 === 2 && day !== 12) suffix = "nd";
  else if (day % 10 === 3 && day !== 13) suffix = "rd";

  if (month === "Invalid Date") return "";

  return `${month} ${day}${suffix}`;
}

export const getSimplifiedAddress = (property) => {
  if (!property?.street_address) return "";

  let address = property.county_owner_address || property.street_address;
  [property.zipcode, property.zip, property.city, property.county_owner_state].forEach((value) => {
    if (typeof value === "string") {
      address = address.replace(new RegExp(`\\b${value}\\b`, "g"), "");
    }
  });

  return address.replace(/,/g, "").trim();
};

const deviceInfoParser = new UAParser();
const deviceInfo = deviceInfoParser.getResult();

export const getDeviceInfo = () => {
  // Native data
  const userAgent = navigator.userAgent;
  const screenSize = `${window.screen.width}x${window.screen.height}`;
  const viewport = `${window.innerWidth}x${window.innerHeight}`;
  const devicePixelRatio = window.devicePixelRatio;
  const language = navigator.language;

  // UAParser.js data
  const browser = `${deviceInfo.browser?.name} ${deviceInfo.browser?.version}`;
  const os = `${deviceInfo.os?.name} ${deviceInfo.os?.version}`;
  const deviceType = deviceInfo.device?.type || "Desktop";
  const deviceVendor = deviceInfo.device?.vendor || "Unknown";
  const deviceModel = deviceInfo.device?.model || "Unknown";

  const data = `
    User Agent: ${userAgent}
    Screen: ${screenSize}
    Viewport: ${viewport}
    Pixel Ratio: ${devicePixelRatio}
    Language: ${language}

    Browser: ${browser}
    OS: ${os}
    Device Type: ${deviceType}
    Device Vendor: ${deviceVendor}
    Device Model: ${deviceModel}
`;

  return data.trim();
};

export function areAllSavedFiltersEmpty(filters) {
  for (const key in filters) {
    if (Array.isArray(filters[key]) && filters[key].length > 0) {
      return false;
    }
    if (filters[key] === null) {
      continue;
    }
    if (typeof filters[key] === "object" && filters[key] !== null) {
      const hasNonEmptyValue = Object.values(filters[key]).some(
        (value) => (Array.isArray(value) && value.length > 0) || (value !== null && value !== "")
      );
      if (hasNonEmptyValue) {
        return false;
      }
    } else if (filters[key] !== "" && filters[key] !== null) {
      return false;
    }
  }
  return true;
}

export const generateParams = ({
  property_id,
  sortData,
  residentType,
  appliedFilters,
  selects,
  ranges,
  dates,
  floats,
}) => {
  const params = {
    is_sale: residentType === RESIDENT_TYPES.equity ? 0 : 1,
    property_id,
    order_column: sortData.field,
    direction: sortData.direction === 1 ? "ASC" : "DESC",
  };

  params.is_filtered = areAllSavedFiltersEmpty(appliedFilters) ? 0 : 1;

  selects?.forEach((selectItem) => {
    const key = selectItem?.description;
    let value = appliedFilters?.[key];

    if (key === "market_area_code" && value) {
      value = value.map((option) => option.split(" - ")[0]);
    }

    if (value && value.length > 0) {
      params[key] = value;
    }
  });

  ranges?.forEach((range) => {
    const key = range?.description;
    let value = appliedFilters?.[key];

    if (value && typeof value === "object" && "start" in value && "end" in value) {
      const start = value.start !== -Infinity ? value.start : undefined;
      const end = value.end !== Infinity ? value.end : undefined;

      if ((start !== undefined && start !== null) || (end !== undefined && end !== null)) {
        params[key] = { start, end };
      }
    } else if (value !== undefined && value !== null && value !== "") {
      params[key] = value;
    }
  });

  dates?.forEach((date) => {
    const key = date?.description;
    let value = appliedFilters?.[key];

    if (value && typeof value === "object" && "start" in value && "end" in value) {
      const start = value.start !== -Infinity ? value.start : undefined;
      const end = value.end !== Infinity ? value.end : undefined;

      if ((start !== undefined && start !== null) || (end !== undefined && end !== null)) {
        params[key] = { start, end };
      }
    } else if (value !== undefined && value !== null && value !== "") {
      params[key] = value;
    }
  });

  floats?.forEach((float) => {
    const key = float?.description;
    let value = appliedFilters?.[key];

    if (value) {
      params[key] = value;
    }
  });

  return params;
};

export const getPrintFileName = (accountNumber, packetCounty) => {
  const today = new Date().toISOString().split("T")[0];
  return packetCounty?.toLowerCase() === "dallas"
    ? `${accountNumber}.pdf`
    : `${accountNumber}_${today}_${packetCounty}.pdf`;
};
